export type RecipeMetaRenameField = "name" | "code" | "both";

export type RecipeMetaRenameInput = {
  field: RecipeMetaRenameField;
  find: string;
  replace: string;
  caseInsensitive?: boolean;
};

export type RecipeMetaRenameRow = {
  id: string;
  code: string;
  name: string;
  nextCode: string;
  nextName: string;
  matched: boolean;
};

export type RecipeMetaRenameBlocker = {
  id: string;
  code: string;
  error: string;
};

export type RecipeMetaRenamePreview = {
  rows: RecipeMetaRenameRow[];
  matchedCount: number;
  blockers: RecipeMetaRenameBlocker[];
};

function applyFindReplace(
  value: string,
  find: string,
  replace: string,
  caseInsensitive: boolean,
): { next: string; matched: boolean } {
  if (!find) return { next: value, matched: false };
  if (caseInsensitive) {
    const escaped = find.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(escaped, "gi");
    if (!re.test(value)) return { next: value, matched: false };
    re.lastIndex = 0;
    return { next: value.replace(re, replace), matched: true };
  }
  if (!value.includes(find)) return { next: value, matched: false };
  return { next: value.split(find).join(replace), matched: true };
}

/**
 * Preview name/code find-replace across selected recipes.
 * Blockers: blank code after rename, or colliding next codes (within the batch).
 * Caller should also reject collisions against the rest of the org catalog.
 */
export function previewRecipeMetaRename(
  recipes: readonly { id: string; code: string; name: string }[],
  input: RecipeMetaRenameInput,
  opts?: { existingCodesOutsideSelection?: ReadonlySet<string> },
): RecipeMetaRenamePreview {
  const find = input.find;
  const replace = input.replace;
  const caseInsensitive = input.caseInsensitive !== false;
  const touchName = input.field === "name" || input.field === "both";
  const touchCode = input.field === "code" || input.field === "both";

  const rows: RecipeMetaRenameRow[] = recipes.map((r) => {
    let nextName = r.name;
    let nextCode = r.code;
    let matched = false;
    if (touchName) {
      const n = applyFindReplace(r.name, find, replace, caseInsensitive);
      nextName = n.next;
      matched = matched || n.matched;
    }
    if (touchCode) {
      const c = applyFindReplace(r.code, find, replace, caseInsensitive);
      nextCode = c.next.trim();
      matched = matched || c.matched;
    }
    return {
      id: r.id,
      code: r.code,
      name: r.name,
      nextCode,
      nextName,
      matched,
    };
  });

  const blockers: RecipeMetaRenameBlocker[] = [];

  if (touchCode) {
    // Final code per selected recipe after rename (unmatched keep current).
    const finalById = new Map(rows.map((r) => [r.id, r.nextCode] as const));
    const owners = new Map<string, string>(); // lower(final) -> id
    for (const row of rows) {
      const final = finalById.get(row.id) ?? row.code;
      const key = final.toLowerCase();
      if (!key) continue;
      const prior = owners.get(key);
      if (prior) owners.set(key, `${prior}\0${row.id}`);
      else owners.set(key, row.id);
    }

    for (const row of rows) {
      if (!row.matched) continue;
      if (!row.nextCode) {
        blockers.push({
          id: row.id,
          code: row.code,
          error: "Rename would leave recipe code blank.",
        });
        continue;
      }
      const key = row.nextCode.toLowerCase();
      const ownerBlob = owners.get(key) ?? "";
      const ownerIds = ownerBlob.split("\0").filter(Boolean);
      if (ownerIds.length > 1) {
        blockers.push({
          id: row.id,
          code: row.code,
          error: `Code collision with another selected recipe (${row.nextCode}).`,
        });
        continue;
      }
      const outside = opts?.existingCodesOutsideSelection;
      if (outside && outside.has(key) && key !== row.code.toLowerCase()) {
        blockers.push({
          id: row.id,
          code: row.code,
          error: `Code already in use: ${row.nextCode}.`,
        });
      }
    }
  }

  return {
    rows,
    matchedCount: rows.filter((r) => r.matched).length,
    blockers,
  };
}
